/* Cookie consent (config only) */
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../plugins/cookieconsent-init';

/**
 * Lightweight vanilla interactions for the α:one landing page.
 * Replaces the previous Locomotive Scroll + GSAP stack. All motion is CSS-driven;
 * this module only toggles classes and wires the interactive controls.
 */
const UTILS = {
    init() {
        this.reveal();
        this.mobileMenu();
        this.faqAccordion();
        this.capabilitiesToggle();
        this.initiativesRail();
        this.modals();
        this.copyPrintYear();
    },

    /* Scroll-reveal: .sr-h/.sr-card/.sr-col/.sr-vis fade+rise once in view. */
    reveal() {
        const els = document.querySelectorAll('.sr-h,.sr-card,.sr-col,.sr-vis');
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce || !('IntersectionObserver' in window)) {
            els.forEach((el) => el.classList.add('in'));
            return;
        }
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    e.target.classList.add('in');
                    io.unobserve(e.target);
                }
            });
        }, { threshold: 0.18, rootMargin: '0px 0px -10% 0px' });
        els.forEach((el) => io.observe(el));
    },

    /* Mobile nav drawer (below lg). Uses inline display so it wins over the
       `.ao-menu { display:block }` base rule; the lg `!important` rule still hides it. */
    mobileMenu() {
        const burger = document.getElementById('ao-burger');
        const menu = document.getElementById('ao-mobile-menu');
        if (!burger || !menu) return;
        const open = () => { menu.style.display = 'block'; burger.setAttribute('aria-expanded', 'true'); };
        const close = () => { menu.style.display = 'none'; burger.setAttribute('aria-expanded', 'false'); };
        burger.addEventListener('click', () => { (menu.style.display === 'none' ? open : close)(); });
        menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
    },

    /* Minimal modal controller (replaces flowbite): [data-modal-target] shows,
       [data-modal-hide] hides the referenced modal by id. */
    modals() {
        document.querySelectorAll('[data-modal-target]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                // prevent the Terms link (nested in the checkbox label) from toggling the box
                e.preventDefault();
                const m = document.getElementById(btn.getAttribute('data-modal-target'));
                if (m) m.classList.remove('hidden');
            });
        });
        document.querySelectorAll('[data-modal-hide]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const m = document.getElementById(btn.getAttribute('data-modal-hide'));
                if (m) m.classList.add('hidden');
            });
        });
    },

    /* Single-open FAQ accordion; plus icon rotates 45deg when open. */
    faqAccordion() {
        const items = document.querySelectorAll('.ao-faq-item');
        items.forEach((item) => {
            const btn = item.querySelector('.ao-faq-q');
            if (!btn) return;
            btn.addEventListener('click', () => {
                const isOpen = item.classList.contains('is-open');
                items.forEach((it) => it.classList.remove('is-open'));
                if (!isOpen) item.classList.add('is-open');
            });
        });
    },

    /* "See all capabilities" expand/collapse. */
    capabilitiesToggle() {
        const btn = document.getElementById('ao-cap-toggle');
        const full = document.getElementById('ao-cap-full');
        if (!btn || !full) return;
        const label = btn.querySelector('.ao-cap-label');
        btn.addEventListener('click', () => {
            const willShow = full.classList.contains('hidden');
            full.classList.toggle('hidden');
            btn.classList.toggle('is-open', willShow);
            btn.setAttribute('aria-expanded', String(willShow));
            if (label) label.textContent = willShow ? 'Hide capabilities' : 'See all capabilities';
        });
    },

    /* Collapsible Initiatives rail (interactive below lg; always expanded at lg+). */
    initiativesRail() {
        const panel = document.querySelector('.ao-init-panel');
        const head = document.querySelector('.ao-init-head');
        if (!panel || !head) return;
        head.addEventListener('click', () => {
            if (window.matchMedia('(min-width: 1024px)').matches) return;
            const collapsed = panel.classList.toggle('is-collapsed');
            head.setAttribute('aria-expanded', String(!collapsed));
        });
    },

    /* Footer year. */
    copyPrintYear() {
        const el = document.querySelector('.year');
        if (el) el.textContent = new Date().getFullYear();
    },
};

export { UTILS };
